import * as React from 'react';
import { useParams } from 'react-router-dom';
import { Spinner } from '@ixicare/ui';
import connect from '~/store/connect';
import crumbs from '~config/crumbs';
import SiteStore from '../store/Site';
import store from '~store';
import { useBreadcrumbPrefix } from '~components/DashboardWrapper/Breadcrumbs';
import { findOrganizationPath, useOrganizations } from '../hooks/useOrganizations';

export interface SiteWrapperProps {
  site: SiteStore;
}

interface SiteWrapperOptions {
  /** Render no chrome above the page — for pages that cover it anyway, like the draw tool. */
  detachChrome?: boolean;
}

const siteWrapper = (Component, getInitialProps?: (props: any) => any, { detachChrome }: SiteWrapperOptions = {}) => {
  const WrappedComponent = ({ sites, loadSites, sitesLoaded, ...props }) => {
    const { id, planeId } = useParams<{ id: string; planeId?: string }>();
    const parsedPlaneId = planeId ? parseInt(planeId, 10) : undefined;

    React.useEffect(() => {
      if (!sitesLoaded) {
        loadSites();
      }
    }, [sitesLoaded, loadSites]);

    const site = React.useMemo(() => {
      return sites.find((s) => s.uuid === id);
    }, [sites, id]);

    // Set the selected site in the store when viewing a site
    React.useEffect(() => {
      if (site) {
        store.sites.setSelectedSiteUuid(site.uuid);
      }
    }, [site]);

    const { data: organizations } = useOrganizations();
    const orgPath = React.useMemo(
      () => (id && organizations ? findOrganizationPath(organizations, (o) => o.sites.some((s) => s.uuid === id)) : []),
      [organizations, id]
    );

    // The layout renders the trail; this wrapper only contributes the crumbs it knows about. Hooks
    // cannot sit behind the spinner return below, so an unresolved site registers nothing.
    useBreadcrumbPrefix(
      site && !detachChrome
        ? [
            crumbs.sites(),
            ...orgPath.map(crumbs.organization),
            { label: site.name, href: `/sites/${site.uuid}#floors` },
          ]
        : []
    );

    if (!site) {
      return <Spinner label={'Loading'} />;
    }

    const additionalProps = getInitialProps ? getInitialProps({ query: { id, planeId } }) : {};

    return <Component {...props} {...additionalProps} site={site} siteId={site.id} planeId={parsedPlaneId} />;
  };

  return connect((state: any) => ({
    sites: state.sites.sites,
    loadSites: state.sites.loadSites,
    sitesLoaded: state.sites.loaded,
  }))(WrappedComponent);
};

export default siteWrapper;
